import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Check, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageLabels = {
  pool_a: 'POOL A', pool_b: 'POOL B', pool_c: 'POOL C', pool_d: 'POOL D',
  quarterfinal: 'QUARTER-FINALS', semifinal: 'SEMI-FINALS', final: 'FINAL', group: 'GROUP'
};

const FIELDS = ['Field A', 'Field B', 'Field C', 'Field D', 'Field E'];

const refereeRoles = [
  { key: 'main_referee_id', label: 'Center Referee' },
  { key: 'assistant_referee_1_id', label: '1st Assistant' },
  { key: 'assistant_referee_2_id', label: '2nd Assistant' },
  { key: 'fourth_official_id', label: 'Fourth Official' },
  { key: 'match_delegate_id', label: 'Match Delegate' },
];

export default function RefereeAssignmentsPage() {
  const { token, organization } = useAuth();
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [referees, setReferees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignment, setAssignment] = useState({});
  const [selectedField, setSelectedField] = useState('');
  const [saving, setSaving] = useState(false);

  const teamMap = teams.reduce((m, t) => { m[t.team_id] = t; return m; }, {});

  useEffect(() => {
    if (organization && token) {
      fetchData();
    }
  }, [organization, token]);

  const fetchData = async () => {
    const headers = { 'Authorization': `Bearer ${token}` };
    const [mRes, rRes, tRes] = await Promise.all([
      fetch(`${API_URL}/api/matches/${organization.org_id}`, { headers }),
      fetch(`${API_URL}/api/referees/${organization.org_id}`, { headers }),
      fetch(`${API_URL}/api/teams/${organization.org_id}`, { headers }),
    ]);
    if (mRes.ok) setMatches(await mRes.json());
    if (rRes.ok) setReferees(await rRes.json());
    if (tRes.ok) setTeams(await tRes.json());
  };

  const openAssignDialog = (match) => {
    setSelectedMatch(match);
    setAssignment({
      main_referee_id: match.main_referee_id || '',
      assistant_referee_1_id: match.assistant_referee_1_id || '',
      assistant_referee_2_id: match.assistant_referee_2_id || '',
      fourth_official_id: match.fourth_official_id || '',
      match_delegate_id: match.match_delegate_id || '',
    });
    setSelectedField(match.field || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Clean assignment data — convert empty strings to null
      const cleanAssignment = {};
      Object.entries(assignment).forEach(([key, val]) => {
        cleanAssignment[key] = val || null;
      });
      
      // Save referee assignments
      const refRes = await fetch(`${API_URL}/api/matches/${selectedMatch.match_id}/assign-referees`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(cleanAssignment)
      });
      
      // Save field (always send, even if unchanged)
      await fetch(`${API_URL}/api/matches/${selectedMatch.match_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ field: selectedField || null })
      });
      
      if (refRes.ok) {
        toast.success('Assignments saved!');
        setDialogOpen(false);
        fetchData();
      } else {
        const err = await refRes.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch (e) { toast.error('Error saving: ' + e.message); }
    finally { setSaving(false); }
  };

  const getRefereeNameById = (id) => {
    const ref = referees.find(r => r.referee_id === id);
    return ref ? ref.name : null;
  };

  // Group matches by stage
  const stageOrder = ['pool_a', 'pool_b', 'pool_c', 'pool_d', 'quarterfinal', 'semifinal', 'final'];
  const groupedMatches = {};
  matches.forEach(m => {
    const stage = m.stage || 'other';
    if (!groupedMatches[stage]) groupedMatches[stage] = [];
    groupedMatches[stage].push(m);
  });

  const assignedCount = matches.filter(m => m.main_referee_id).length;

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="referee-assignments-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
              Referee Assignments
            </h1>
            <p className="text-gray-600 mt-2">
              Assign officials and fields to matches ({assignedCount}/{matches.length} assigned)
            </p>
          </div>
        </div>

        {referees.length === 0 && (
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 text-orange-400 text-sm">
            <Shield size={16} className="inline mr-2" />
            No referees added yet. Go to the Referees page to add officials first.
          </div>
        )}

        {stageOrder.map(stage => {
          const stageMatches = groupedMatches[stage];
          if (!stageMatches || stageMatches.length === 0) return null;
          
          return (
            <div key={stage}>
              <h2 className="font-heading text-lg font-bold text-black uppercase mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-emerald-500" />
                {stageLabels[stage] || stage}
              </h2>
              <div className="space-y-2">
                {stageMatches.map(match => {
                  const homeName = teamMap[match.home_team_id]?.name || 'TBD';
                  const awayName = teamMap[match.away_team_id]?.name || 'TBD';
                  const hasRef = match.main_referee_id;
                  
                  return (
                    <Card 
                      key={match.match_id} 
                      className={`bg-white border-gray-200 cursor-pointer hover:border-emerald-500/30 transition-all ${hasRef ? 'border-l-2 border-l-emerald-500' : ''}`}
                      onClick={() => openAssignDialog(match)}
                      data-testid={`assign-match-${match.match_id}`}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="text-black font-medium text-sm min-w-[200px]">
                            {homeName} <span className="text-gray-500">vs</span> {awayName}
                          </div>
                          <span className="text-gray-500 text-xs font-mono">{match.match_date} • {match.match_time} MT</span>
                          {match.field && (
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">{match.field}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {hasRef ? (
                            <div className="flex items-center gap-1 text-xs text-emerald-400">
                              <Check size={14} />
                              <span>{getRefereeNameById(match.main_referee_id)}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">No referee assigned</span>
                          )}
                          <Shield size={16} className={hasRef ? 'text-emerald-400' : 'text-gray-500'} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Assignment Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-gray-200 text-black max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">
                Assign Officials
              </DialogTitle>
              {selectedMatch && (
                <p className="text-gray-600 text-sm mt-1">
                  {teamMap[selectedMatch.home_team_id]?.name || 'TBD'} vs {teamMap[selectedMatch.away_team_id]?.name || 'TBD'}
                  <span className="text-gray-500 ml-2">{selectedMatch.match_date}</span>
                </p>
              )}
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Field Selection */}
              <div className="space-y-2">
                <Label className="text-gray-700">Match Field / Venue</Label>
                <Select value={selectedField || "none"} onValueChange={(v) => setSelectedField(v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                    <SelectValue placeholder="Select field" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1b] border-gray-200">
                    <SelectItem value="none" className="text-gray-600">No field assigned</SelectItem>
                    {FIELDS.map(f => (
                      <SelectItem key={f} value={f} className="text-black hover:bg-gray-100">{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Referee Roles */}
              {refereeRoles.map(role => (
                <div key={role.key} className="space-y-2">
                  <Label className="text-gray-700">{role.label}</Label>
                  <Select
                    value={assignment[role.key] || "none"}
                    onValueChange={(v) => setAssignment({...assignment, [role.key]: v === "none" ? "" : v})}
                  >
                    <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                      <SelectValue placeholder="Select referee" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1b] border-gray-200">
                      <SelectItem value="none" className="text-gray-600">Not assigned</SelectItem>
                      {referees.map(ref => (
                        <SelectItem key={ref.referee_id} value={ref.referee_id} className="text-black hover:bg-gray-100">
                          {ref.name} {ref.level ? `(${ref.level})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-gray-200 text-black hover:bg-gray-100">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black">
                  <Check size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Assignments'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
